type PagerAction =
	(
		{ onclick: (e: Event) => void }
		| { href: string }
	) & {
	label: string;
	isCurrent?: boolean;
};

export function generatePagerActions(currentPage: number, pageCount: number, callback: (page: number) => string | ((e: Event) => void), lookahead: number = 2): PagerAction[] {
	const out: PagerAction[] = [];
	for (let i = Math.max(0, currentPage - lookahead); i < Math.min(pageCount, currentPage + 1 + lookahead); i++) {
		const action = callback(i);
		const label = (i + 1).toString();
		const isCurrent = (i === currentPage);
		if (typeof action == "string") {
			out.push({ label, isCurrent, href: action });
		} else {
			out.push({ label, isCurrent, onclick: action });
		}
	}
	return out;
}

export class Pager {
	public readonly element: HTMLElement = document.createElement("div");
	constructor(private previousLabel: string, private nextLabel: string) {
		this.element.classList.add("pager");
		this.element.hidden = true;
	}

	public setActions(actions: PagerAction[]): void {
		this.element.hidden = actions.length < 2;
		this.element.innerHTML = "";

		let next: PagerAction = { href: "", label: "" };
		let previous: PagerAction = { href: "", label: "" };
		let maxNeighbors: number = 1;
		let padLeft: number = 0;
		let padRight: number = 0;

		const elements = actions.map((action, i) => {
			const el = this.makeActionElement(action, [ "pager_link" ]);
			if (action.isCurrent) {
				next =     actions[i + 1] ?? next;
				previous = actions[i - 1] ?? previous;
				maxNeighbors = Math.max(maxNeighbors, i, actions.length - i - 1);
				padRight = maxNeighbors - (actions.length - i - 1);
				padLeft = maxNeighbors - i;
			}
			return el;
		});

		this.element.append(
			this.makeActionElement(
				{ ...previous, label: this.previousLabel },
				[ "pager_rocker", "-previous", "-pad-right-" + padLeft ]
			),
			...elements,
			this.makeActionElement(
				{ ...next, label: this.nextLabel },
				[ "pager_rocker", "-next", "-pad-left-" + padRight ]
			)
		);
	}

	private makeActionElement(action: PagerAction, classes: string[] = []): HTMLElement {
		const element = ((): HTMLElement => {
			if ("href" in action && action.href) {
				const a = document.createElement("a");
				a.href = action.href;
				return a;
			}
			const button = document.createElement("button");
			button.type = "button";
			if ("onclick" in action) {
				button.addEventListener("click", action.onclick);
			} else {
				button.setAttribute("aria-disabled", "true");
				button.classList.add("-disabled");
			}
			return button;
		})();
		element.classList.add(...classes);
		if (action.isCurrent) {
			element.classList.add("-current");
		}
		element.innerText = action.label;
		return element;
	}
}
